/**
 * IframeEventListener
 *
 * Mounts early in the app (via GlobalComponentsWrapper, gated by IS_WHITELABEL_APP).
 * Listens for `amber_custom_event` postMessage events from the parent window
 * (partner site) and stores the payload in memory for inclusion in outgoing URLs.
 *
 * This component uses a direct window event listener (not useParentMessageListener)
 * because partners may send data before iframe detection completes in Redux.
 * Since this component only mounts in whitelabel builds, it's safe to listen
 * unconditionally.
 *
 * It also replays any messages that arrived before React hydrated by checking
 * a global buffer that an inline script populates (see renderHtml.ts).
 *
 * Message format expected from partner:
 * {
 *   type: 'amber_custom_event',
 *   payload: { student_id: '123', counsellor: 'Jane', ... }
 * }
 */

import { useEffect } from "react";
import { setIframeEventData, hydrateIframeEventDataFromUrl } from "@Utils/iframeEventData";

const AMBER_CUSTOM_EVENT_TYPE = "amber_custom_event";

const IframeEventListener = (): null => {
  useEffect(() => {
    if (typeof window === "undefined") return;

    // On page load, decode _ied from URL → localStorage (first-party pages)
    hydrateIframeEventDataFromUrl();

    // Replay any buffered messages that arrived before React mounted
    const { __AMBER_EVENT_BUFFER__: buffered } = window as any;
    if (Array.isArray(buffered) && buffered.length > 0) {
      buffered.forEach((payload: unknown) => {
        if (payload && typeof payload === "object" && !Array.isArray(payload)) {
          setIframeEventData(payload as Record<string, unknown>);
        }
      });
      // Clear buffer so we don't replay twice
      (window as any).__AMBER_EVENT_BUFFER__ = undefined;
    }

    // Listen for future messages
    const handleMessage = (event: MessageEvent) => {
      const { data } = event;
      if (
        data &&
        data.type === AMBER_CUSTOM_EVENT_TYPE &&
        data.payload &&
        typeof data.payload === "object" &&
        !Array.isArray(data.payload)
      ) {
        setIframeEventData(data.payload as Record<string, unknown>);
      }
    };

    window.addEventListener("message", handleMessage);
    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  return null;
};

export default IframeEventListener;
